{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2019 - 2025                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.TMSFNCSpinEdit;

{$I WEBLib.TMSFNCDefines.inc}

interface

uses
  Classes,
  {$IFNDEF LCLLIB}
  Types,
  {$ENDIF}
  {$IFDEF FMXLIB}
  FMX.Types,
  {$ENDIF}
  {$IFDEF CMNLIB}
  ExtCtrls,
  {$ENDIF}
  {$IFDEF WEBLIB}
  WEBLib.Controls, WEBLib.ExtCtrls,
  {$ENDIF}
  WEBLib.TMSFNCCustomControl, WEBLib.TMSFNCGraphics, WEBLib.TMSFNCTypes,
  WEBLib.TMSFNCGraphicsTypes, WEBLib.TMSFNCEdit, WEBLib.TMSFNCStyles, WEBLib.TMSFNCTrackBar;

const
  MAJ_VER = 1; // Major version nr.
  MIN_VER = 0; // Minor version nr.
  REL_VER = 1; // Release nr.
  BLD_VER = 4; // Build nr.

  //Version history
  //v1.0.0.0 : First Release
  //v1.0.0.1 : Fixed : Issue with control color in disabled state
  //v1.0.0.2 : Fixed : Issue with frequency in editable mode
  //v1.0.0.3 : Fixed : Edit field alignment on mobiles
  //         : Fixed : Issue with OnValueChanged event
  //v1.0.0.4 : Fixed : Issue with EditFieldPrecision persistance
  //v1.0.1.0 : New : Support for high dpi
  //v1.0.1.1 : Fixed : Issue with OnChanged event not called when typing in the edit field
  //v1.0.1.2 : Fixed : Issue with 0 value not shown in Delphi 12
  //v1.0.1.3 : Improved : Exposed Edit property
  //v1.0.1.4 : Fixed : Issue with value setting in FMX

type
  TTMSFNCCustomSpinEditAppearance = class(TTMSFNCCustomTrackBarBaseAppearance)
  published
    property ButtonSize;
    property ButtonFill;
    property ButtonSpacing;
    property ButtonDownFill;
    property ButtonDownStroke;
    property ButtonStroke;
    property ButtonShape;
    property MinButtonBitmap;
    property MinButtonDownBitmap;
    property PlusButtonBitmap;
    property PlusButtonDownBitmap;
    property ValueFont;
    property Orientation;
  end;

  TTMSFNCCustomSpinEditInteraction = class(TTMSFNCCustomTrackBarBaseInteraction)
  published
    property Frequency;
    property Editable;
    property RepeatClick;
    property RepeatInterval;
  end;

  TTMSFNCCustomSpinEditBeforeDrawValueEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; AValue: Single; var AAllow: Boolean; var ADefaultDraw: Boolean) of object;
  TTMSFNCCustomSpinEditAfterDrawValueEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; AValue: Single) of object;

  TTMSFNCCustomSpinEdit = class(TTMSFNCCustomTrackBar)
  private
    FEdit: TTMSFNCEdit;
    FSpinAppearance: TTMSFNCCustomSpinEditAppearance;
    FSpinInteraction: TTMSFNCCustomSpinEditInteraction;
    FOnBeforeDrawValue: TTMSFNCCustomSpinEditBeforeDrawValueEvent;
    FOnAfterDrawValue: TTMSFNCCustomSpinEditAfterDrawValueEvent;
    procedure SetSpinAppearance(const AValue: TTMSFNCCustomSpinEditAppearance);
    procedure SetSpinInteraction(const AValue: TTMSFNCCustomSpinEditInteraction);
    procedure SetEditFieldPrecision(const AValue: SmallInt);
    procedure SetEditFieldPrecisionDisplay(const AValue: TTMSFNCEditPrecisionDisplay);
    function GetEditFieldPrecision: SmallInt;
    function GetEditFieldPrecisionDisplay: TTMSFNCEditPrecisionDisplay;
    {$IFDEF FMXLIB}
    procedure SetEditFieldKeyboardType(const AValue: TVirtualKeyboardType);
    function GetEditFieldKeyboardType: TVirtualKeyboardType;
    {$ENDIF}
  protected
    procedure ChangeDPIScale(M, D: Integer); override;
    procedure UpdateControlAfterResize; override;
    procedure Loaded; override;
    procedure Draw({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); override;
    procedure DrawTrackLine({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); override;
    procedure DrawTrackSliders({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); override;
    procedure ResizeEditField(ARect: TRectF);
    procedure DrawValueText({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF);
    procedure DoBeforeDrawValue({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF; AValue: Single; var AAllow: Boolean; var ADefaultDraw: Boolean); virtual;
    procedure DoAfterDrawValue({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF; AValue: Single); virtual;
    procedure DoValueChanged(AValue: Single); override;
    {$IFDEF FMXLIB}
    procedure EditTyping(Sender: TObject);
    {$ENDIF}
    {$IFNDEF FMXLIB}
    procedure EditKeyDown(Sender: TObject; var {%H-}Key: Word; {%H-}Shift: TShiftState);
    {$ENDIF}
    procedure UpdateEditField;
    procedure EditExit(Sender: TObject);
    procedure SpinInteractionChange(Sender: TObject);
    procedure SpinAppearanceChange(Sender: TObject);
    function GetTrackLineRect: TRectF; override;
    function GetMaxButtonRect: TRectF; override;
    function GetMinButtonRect: TRectF; override;
    property EditFieldPrecision: SmallInt read GetEditFieldPrecision write SetEditFieldPrecision default 1;
    property EditFieldPrecisionDisplay: TTMSFNCEditPrecisionDisplay read GetEditFieldPrecisionDisplay write SetEditFieldPrecisionDisplay default pdNormal;
    property Appearance: TTMSFNCCustomSpinEditAppearance read FSpinAppearance write SetSpinAppearance;
    property Interaction: TTMSFNCCustomSpinEditInteraction read FSpinInteraction write SetSpinInteraction;
    property Edit: TTMSFNCEdit read FEdit;
    {$IFDEF FMXLIB}
    property EditFieldKeyboardType: TVirtualKeyboardType read GetEditFieldKeyboardType write SetEditFieldKeyboardType;
    {$ENDIF}
    property OnBeforeDrawValue: TTMSFNCCustomSpinEditBeforeDrawValueEvent read FOnBeforeDrawValue write FOnBeforeDrawValue;
    property OnAfterDrawValue: TTMSFNCCustomSpinEditAfterDrawValueEvent read FOnAfterDrawValue write FOnAfterDrawValue;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
  end;

  {$IFNDEF LCLLIB}
  [ComponentPlatformsAttribute(TMSPlatformsWeb)]
  {$ENDIF}
  TTMSFNCSpinEdit = class(TTMSFNCCustomSpinEdit)
  public
    property Edit;
  published
    property Appearance;
    property Interaction;
    property EditFieldPrecision;
    property EditFieldPrecisionDisplay;
    {$IFDEF FMXLIB}
    property EditFieldKeyboardType;
    {$ENDIF}
    property Fill;
    property Stroke;
    property Max;
    property Min;
    property Value;
    property OnChanged;
    property OnBeforeDrawButton;
    property OnAfterDrawButton;
    property OnBeforeDrawValue;
    property OnAfterDrawValue;
    property OnValueChanged;
  end;

implementation

uses
  SysUtils, WEBLib.TMSFNCUtils
  {$IFDEF VCLLIB}
  ,VCL.Graphics
  {$ENDIF}
  {$IFNDEF LCLLIB}
  ,Math
  {$ENDIF}
  ;

{ TTMSFNCCustomSpinEdit }

procedure TTMSFNCCustomSpinEdit.ChangeDPIScale(M, D: Integer);
begin
  inherited;
  BeginUpdate;
  Appearance.ValueFont.Height := TTMSFNCUtils.MulDivInt(Appearance.ValueFont.Height, M,D);
  Appearance.ButtonSize := TTMSFNCUtils.MulDivInt(Appearance.ButtonSize, M, D);
  EndUpdate;
end;

constructor TTMSFNCCustomSpinEdit.Create(AOwner: TComponent);
begin
  inherited;
  Width := 75;
  Height := 22;

  FEdit := TTMSFNCEdit.Create(Self);
  FEdit.Parent := Self;
  {$IFDEF FMXLIB}
  FEdit.Stored := False;
  FEdit.TextSettings.HorzAlign := TTextAlign.Center;
  FEdit.OnChangeTracking := @EditTyping;
  FEdit.OnChange := @EditExit;
  {$ENDIF}
  {$IFNDEF FMXLIB}
  FEdit.Alignment := taCenter;
  FEdit.OnKeyUp := @EditKeyDown;
  {$ENDIF}
  FEdit.EditType := etSignedFloat;
  FEdit.Precision := 1;
  FEdit.Text := FloatToStr(Value);
  FEdit.OnExit := @EditExit;

  FSpinAppearance := TTMSFNCCustomSpinEditAppearance.Create(Self);
  FSpinAppearance.ButtonStroke.Color := gcGray;
  FSpinAppearance.ButtonDownStroke.Color := gcGray;
  FSpinAppearance.OnChange := @SpinAppearanceChange;

  FSpinInteraction := TTMSFNCCustomSpinEditInteraction.Create(Self);
  FSpinInteraction.OnChange := @SpinInteractionChange;

  FSpinAppearance.ButtonShape := bsRectangle;
  FSpinAppearance.TickMarkPosition := tmpNone;

  UpdateEditField;
end;

destructor TTMSFNCCustomSpinEdit.Destroy;
begin
  FEdit.Free;
  FSpinAppearance.Free;
  FSpinInteraction.Free;
  inherited;
end;

procedure TTMSFNCCustomSpinEdit.DoAfterDrawValue(AGraphics: TTMSFNCGraphics;
  ARect: TRectF; AValue: Single);
begin
  if Assigned(OnAfterDrawValue) then
    OnAfterDrawValue(Self, AGraphics, ARect, AValue);
end;

procedure TTMSFNCCustomSpinEdit.DoBeforeDrawValue(AGraphics: TTMSFNCGraphics;
  ARect: TRectF; AValue: Single; var AAllow, ADefaultDraw: Boolean);
begin
  if Assigned(OnBeforeDrawValue) then
    OnBeforeDrawValue(Self, AGraphics, ARect, AValue, AAllow, ADefaultDraw);
end;

procedure TTMSFNCCustomSpinEdit.DoValueChanged(AValue: Single);
begin
  inherited;
  FEdit.Text := FloatToStr(AValue);
end;

procedure TTMSFNCCustomSpinEdit.Draw(AGraphics: TTMSFNCGraphics; ARect: TRectF);
var
  r: TRectF;
begin
  inherited;
  r := GetTrackLineRect;

  if Interaction.Editable then
  begin
    if IsExporting then
      DrawValueText(AGraphics, r);
  end
  else
    DrawValueText(AGraphics, r);
end;

procedure TTMSFNCCustomSpinEdit.ResizeEditField(ARect: TRectF);
begin
  {$IFDEF FMXLIB}
  FEdit.TextSettings.Font.Assign(Appearance.ValueFont);
  FEdit.TextSettings.FontColor := Appearance.ValueFont.Color;
  FEdit.Position.X := ARect.Left;
  FEdit.Width := ARect.Right - ARect.Left;
  {$ENDIF}
  {$IFNDEF FMXLIB}
  FEdit.Font.Assign(Appearance.ValueFont);
  FEdit.Left := Round(ARect.Left);
  FEdit.Width := Round(ARect.Right - ARect.Left);
  {$ENDIF}
  case Appearance.ButtonShape of
    bsRounded:
    begin
      {$IFNDEF FMXMOBILE}
      FEdit.Height := Appearance.ButtonSize;
      {$ENDIF}
      {$IFDEF FMXLIB}
      FEdit.Position.Y := ARect.Top + (ARect.Bottom - ARect.Top - FEdit.Height) / 2;
      {$ENDIF}
      {$IFNDEF FMXLIB}
      FEdit.Top := Round(ARect.Top + (ARect.Bottom - ARect.Top - FEdit.Height) / 2);
      {$ENDIF}
    end;
    bsRectangle:
    begin
      {$IFDEF FMXLIB}
      {$IFNDEF FMXMOBILE}
      FEdit.Height := ARect.Bottom - ARect.Top;
      FEdit.Position.Y := ARect.Top;
      {$ENDIF}
      {$IFDEF FMXMOBILE}
      FEdit.Position.Y := ARect.Top + (ARect.Bottom - ARect.Top - FEdit.Height) / 2;
      {$ENDIF}
      {$ENDIF}
      {$IFNDEF FMXLIB}
      FEdit.Top := Round(ARect.Top);
      FEdit.Height := Round(ARect.Bottom - ARect.Top);
      {$ENDIF}
    end;
  end;
  FEdit.Visible := True;
end;

procedure TTMSFNCCustomSpinEdit.DrawValueText(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
var
  b, df: Boolean;
  s: string;
begin
  s := Format('%g', [FEdit.FloatValue]);
  AGraphics.Font.AssignSource(Appearance.ValueFont);

  b := True;
  df := True;
  DoBeforeDrawValue(AGraphics, ARect, StrToFloat(s), b, df);
  if b then
  begin
    if df then
      AGraphics.DrawText(ARect, s, False, gtaCenter);

    DoAfterDrawValue(AGraphics, ARect, StrToFloat(s));
  end;

  FEdit.Visible := False;
end;

procedure TTMSFNCCustomSpinEdit.EditExit(Sender: TObject);
var
  v: Single;
  b: Boolean;
begin
  b := TryStrToFloat(FEdit.Text, v);
  if (FEdit.Text = '') or not b then
  begin
    if FValues.Left <> Min then
    begin
      FValues.Left := Min;
      DoValuesChanged(FValues.Left, True);
    end
    else
      FEdit.Text := FloatToStr(Min);
  end
  else
  begin
    if (v <= Min) then
    begin
      if FValues.Left <> Min then
      begin
        FValues.Left := Min;
        DoValuesChanged(FValues.Left, True);
      end
      else
        FEdit.Text := FloatToStr(Min);
    end
    else if (v >= Max) then
    begin
      if FValues.Left <> Max then
      begin
        FValues.Left := Max;
        DoValuesChanged(FValues.Left, True);
      end
      else
        FEdit.Text := FloatToStr(Max);
    end
    else
    begin
      if FValues.Left <> v then
      begin
        FValues.Left := v;
        DoValuesChanged(FValues.Left, True);
      end
      else
        FEdit.Text := FloatToStr(v);
    end;
  end;
end;

{$IFDEF FMXLIB}
procedure TTMSFNCCustomSpinEdit.EditTyping(Sender: TObject);
{$ENDIF}
{$IFNDEF FMXLIB}
procedure TTMSFNCCustomSpinEdit.EditKeyDown(Sender: TObject; var Key: Word; {%H-}Shift: TShiftState);
{$ENDIF}
var
  v: Single;
begin
  {$IFNDEF FMXLIB}
  if Key = KEY_RETURN then
  begin
    EditExit(Self);
    Exit;
  end;
  {$ENDIF}
  if not TryStrToFloat(FEdit.Text, v) then
  begin
    if (FEdit.Text <> '') and (FEdit.Text <> '-') then
    begin
      FValues.Left := Min;
      DoValuesChanged(FValues.Left, True);
    end;
    DoChanged;
  end
  else
    DoChanged;
end;

procedure TTMSFNCCustomSpinEdit.DrawTrackLine(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
begin
  //
end;

procedure TTMSFNCCustomSpinEdit.DrawTrackSliders(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
begin
  //
end;

function TTMSFNCCustomSpinEdit.GetEditFieldPrecision: SmallInt;
begin
  Result := 1;

  if Assigned(FEdit) then
    Result := FEdit.Precision;
end;

function TTMSFNCCustomSpinEdit.GetEditFieldPrecisionDisplay: TTMSFNCEditPrecisionDisplay;
begin
  Result := pdNormal;

  if Assigned(FEdit) then
    Result := FEdit.PrecisionDisplay;
end;

{$IFDEF FMXLIB}
function TTMSFNCCustomSpinEdit.GetEditFieldKeyboardType: TVirtualKeyboardType;
begin
  Result := TVirtualKeyboardType.Default;

  if Assigned(FEdit) then
    Result := FEdit.KeyboardType;
end;
{$ENDIF}

function TTMSFNCCustomSpinEdit.GetMaxButtonRect: TRectF;
var
  cr: TRectF;
begin
  if Appearance.ButtonShape = bsRectangle then
  begin
    cr := GetContentRect;
    case Appearance.Orientation of
      tboHorizontal:
      begin
        {$IFNDEF FMXMOBILE}
        Result := RectF(cr.Right - Appearance.ButtonSize, cr.Top, cr.Right, cr.Bottom);
        {$ENDIF}
        {$IFDEF FMXMOBILE}
        Result := RectF(cr.Right - Appearance.ButtonSize, (cr.Bottom - FEdit.Height) / 2, cr.Right, (cr.Bottom + FEdit.Height) / 2);
        {$ENDIF}
      end;
      tboVertical: Result := RectF(cr.Left, cr.Top, cr.Right, cr.Top + Appearance.ButtonSize);
    end;
  end
  else
    Result := inherited;
end;

function TTMSFNCCustomSpinEdit.GetMinButtonRect: TRectF;
var
  cr: TRectF;
begin
  if Appearance.ButtonShape = bsRectangle then
  begin
    cr := GetContentRect;
    case Appearance.Orientation of
      tboHorizontal:
      begin
        cr := GetContentRect;
        {$IFNDEF FMXMOBILE}
        Result := RectF(cr.Left, cr.Top, cr.Left + Appearance.ButtonSize, cr.Bottom);
        {$ENDIF}
        {$IFDEF FMXMOBILE}
        Result := RectF(cr.Left, (cr.Bottom - FEdit.Height) / 2, cr.Left + Appearance.ButtonSize, (cr.Bottom + FEdit.Height) / 2);
        {$ENDIF}
      end;
      tboVertical: Result := RectF(cr.Left, cr.Bottom - Appearance.ButtonSize, cr.Right, cr.Bottom);
    end;
  end
  else
    Result := inherited;
end;

function TTMSFNCCustomSpinEdit.GetTrackLineRect: TRectF;
begin
  Result := inherited;
  case Appearance.Orientation of
    tboHorizontal: InflateRectEx(Result, SLIDERLINESPACING, 0);
    tboVertical: InflateRectEx(Result, 0, SLIDERLINESPACING);
  end;
end;

procedure TTMSFNCCustomSpinEdit.Loaded;
begin
  inherited;
  UpdateEditField;
end;

procedure TTMSFNCCustomSpinEdit.SetEditFieldPrecision(const AValue: SmallInt);
begin
  if Assigned(FEdit) then
    FEdit.Precision := AValue;
end;

procedure TTMSFNCCustomSpinEdit.SetEditFieldPrecisionDisplay(
  const AValue: TTMSFNCEditPrecisionDisplay);
begin
  if Assigned(FEdit) then
    FEdit.PrecisionDisplay := AValue;
end;

{$IFDEF FMXLIB}
procedure TTMSFNCCustomSpinEdit.SetEditFieldKeyboardType(const AValue: TVirtualKeyboardType);
begin
  if Assigned(FEdit) then
    FEdit.KeyboardType := AValue;
end;
{$ENDIF}

procedure TTMSFNCCustomSpinEdit.SetSpinAppearance(
  const AValue: TTMSFNCCustomSpinEditAppearance);
begin
  FSpinAppearance.Assign(AValue);
end;

procedure TTMSFNCCustomSpinEdit.SetSpinInteraction(
  const AValue: TTMSFNCCustomSpinEditInteraction);
begin
  FSpinInteraction.Assign(AValue);
end;

procedure TTMSFNCCustomSpinEdit.SpinAppearanceChange(Sender: TObject);
begin
  FAppearance.Assign(FSpinAppearance);
  UpdateEditField;
  Invalidate;
end;

procedure TTMSFNCCustomSpinEdit.SpinInteractionChange(Sender: TObject);
begin
  FInteraction.Assign(FSpinInteraction);
  UpdateEditField;
  Invalidate;
end;

procedure TTMSFNCCustomSpinEdit.UpdateControlAfterResize;
begin
  inherited;
  if Assigned(FEdit) then
  begin
    UpdateEditField;
  end;
end;

procedure TTMSFNCCustomSpinEdit.UpdateEditField;
var
  r: TRectF;
begin
  if FSpinInteraction.Editable then
  begin
    r := GetTrackLineRect;
    ResizeEditField(r);
  end;
end;

end.
